/**
 * La forma de un tema, y de dónde salen sus valores.
 *
 * Vivía dentro de `editor.store.ts`, en la aplicación del editor. Mientras el
 * único que pintaba con un tema era el editor eso estaba bien; deja de estarlo en
 * cuanto el panel tiene que enseñar la marca del sitio y la página de reserva
 * tiene que heredarla. Copiar la derivación en cada sitio garantiza que un día
 * digan colores distintos del mismo proyecto.
 *
 * Aquí no hay estado ni React: entran valores y salen valores. Por eso se puede
 * probar entero sin levantar nada, y por eso lo pueden usar el editor, el panel y
 * el worker sin arrastrarse unos a otros.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import type { InteractionStates, StateTransition } from './interactionStates'
import type { SiteMotion } from './motion'

export type ThemeRole =
  | 'section'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'
  | 'paragraph'
  | 'eyebrow'
  | 'smallText'
  | 'buttonPrimary'
  | 'buttonSecondary'
  | 'card'
  | 'image'
  | 'icon'
  | 'badge'
  | 'rating'
  | 'avatarGroup'
  | 'stat'
  | 'link'
  | 'navigation'
  | 'footer'

export type ThemeRoleElementProps = {
  iconSize?: number | undefined
  ratingValue?: number | undefined
  ratingSize?: number | undefined
  avatarCount?: number | undefined
  avatarSize?: number | undefined
}

export type PageTheme = {
  presetId?: string
  typography?: {
    fontPairId?: string
    headingFont?: string
    bodyFont?: string
  }
  colors: {
    background: string
    backgroundSecondary: string
    card: string
    surface: string
    border: string
    text: string
    muted: string
    primary: string
    accent: string
  }
  roles: Record<ThemeRole, StyleProperties>
  roleProps?: Partial<Record<ThemeRole, ThemeRoleElementProps>>
  roleBreakpoints?: {
    tablet?: Partial<Record<ThemeRole, StyleProperties>>
    mobile?: Partial<Record<ThemeRole, StyleProperties>>
  }
}

export type GlobalColorComponent = {
  id: string
  /**
   * El nombre, SÓLO cuando lo escribió una persona.
   *
   * Los de fábrica no lo llevan, y eso es lo que arregla el lío: antes cada
   * proyecto guardaba «Primario» o «Primary» según por dónde se hubiera creado
   * —la pantalla normal escribía en español y el constructor con IA en inglés—,
   * y ese texto se quedaba dentro del tema para siempre. Ahora el dato no tiene
   * idioma: guarda `nameKey` y el nombre se dice al pintarlo, en el de quien
   * mira. Dos personas con el mismo proyecto lo ven cada una en el suyo.
   *
   * Un color que añade el cliente sí lo lleva: ése es SU texto y traducirlo
   * sería cambiarle lo que escribió.
   */
  name?: string
  /** Clave de catálogo. La llevan los de fábrica, y manda sobre `name`. */
  nameKey?: string
  value: string
  cssVar: string
  builtIn?: boolean
}

export type GlobalTypographyComponent = {
  id: string
  /** Ver `name` y `nameKey` en `GlobalColorComponent`. */
  name?: string
  nameKey?: string
  tag?: string
  role?: ThemeRole
  styles: StyleProperties
  breakpoints?: {
    tablet?: Partial<StyleProperties>
    mobile?: Partial<StyleProperties>
  }
  builtIn?: boolean
}

export type GlobalButtonComponent = {
  id: string
  /** Ver `name` y `nameKey` en `GlobalColorComponent`. */
  name?: string
  nameKey?: string
  role?: ThemeRole
  styles: StyleProperties
  breakpoints?: {
    tablet?: Partial<StyleProperties>
    mobile?: Partial<StyleProperties>
  }
  builtIn?: boolean
  /**
   * Hover, presionado y enfocado, heredados por todo botón con este estilo.
   * Sin el campo, los de su papel (`defaultButtonStates`); `{}` = ninguno.
   */
  states?: InteractionStates
  /** La transición entre estados. Sin el campo, 200 ms suave. */
  transition?: StateTransition
}

export type GlobalCardComponent = {
  id: string
  /** Ver `name` y `nameKey` en `GlobalColorComponent`. */
  name?: string
  nameKey?: string
  role?: 'card'
  /** Container-only styles. Typography remains owned by global text styles. */
  styles: StyleProperties
  breakpoints?: {
    tablet?: Partial<StyleProperties>
    mobile?: Partial<StyleProperties>
  }
  builtIn?: boolean
  /** Estados heredados por las tarjetas de este estilo. Sin el campo, ninguno. */
  states?: InteractionStates
  transition?: StateTransition
}

export type GlobalSectionComponent = {
  id: string
  /** Ver `name` y `nameKey` en `GlobalColorComponent`. */
  name?: string
  nameKey?: string
  role?: 'section'
  /** Fondo y espaciado vertical, nada más: una sección no tiene borde ni sombra propios. */
  styles: StyleProperties
  /**
   * Hasta dónde llega el contenido de dentro: `'100%'` para todo el ancho, o
   * una medida —`'1280px'`— para limitarlo y centrarlo. Ausente, cada sección
   * decide con su propio `containerWidth`, como antes de existir esto: así un
   * estilo que nadie ha tocado no cambia ninguna página ya hecha.
   */
  contentWidth?: string
  /** El mismo ajuste para tablet y móvil; sin él, heredan del tamaño mayor. */
  contentWidthBreakpoints?: { tablet?: string; mobile?: string }
  breakpoints?: {
    tablet?: Partial<StyleProperties>
    mobile?: Partial<StyleProperties>
  }
  builtIn?: boolean
}

export type GlobalIconComponent = {
  id: string
  /** Ver `name` y `nameKey` en `GlobalColorComponent`. */
  name?: string
  nameKey?: string
  styles: StyleProperties
  iconSize: number
  breakpoints?: {
    tablet?: Partial<StyleProperties>
    mobile?: Partial<StyleProperties>
  }
  breakpointSizes?: {
    tablet?: number
    mobile?: number
  }
  builtIn?: boolean
}

/**
 * The brand mark, as one token every inserted logo follows.
 *
 * The four arrangements the UI offers — logo+nombre, sólo nombre, icono+nombre,
 * icono — are not four settings but two: what the mark is, and whether the name
 * shows next to it. Storing the combination instead would make "hide the name"
 * a different edit depending on which arrangement is active.
 */
export type BrandLogo = {
  name: string
  /** Empty means "follow the h1 role", which is the default a wordmark wants. */
  nameFont: string
  /** A `var(--wb-…)` reference when bound to a palette token, else a literal. */
  nameColor: string
  /** Wordmark colour used when the logo sits on a dark surface. */
  nameColorDark: string
  nameSize: string
  nameWeight: string
  mark: 'none' | 'icon' | 'image'
  iconName: string
  iconColor: string
  /** Glyph colour used when the logo sits on a dark surface. */
  iconColorDark: string
  iconSize: string
  iconBackground: string
  /** Icon plate colour used on dark surfaces. */
  iconBackgroundDark: string
  /** 0–100. Applied to the icon's background only, never to the glyph. */
  iconBackgroundOpacity: number
  /** Corner rounding of the icon's plate. Empty derives one from the size. */
  iconRadius: string
  /** Light artwork, used on light backgrounds. The only one many brands have. */
  imageLight: string
  /** Optional dark-background artwork; falls back to `imageLight` when empty. */
  imageDark: string
  /** Display width of image artwork. Height remains proportional and capped. */
  imageSize: string
  showName: boolean
}

/**
 * La foto inmutable que deja la última combinación de Style elegida.
 *
 * `presetId` pasa a `custom` con el primer retoque, y eso está bien para decir
 * que el tema ya no coincide exactamente con el preset. Pero no alcanza para
 * «volver a como estaba»: para eso conservamos las piezas que la combinación
 * produjo. No incluye el logo porque es contenido de marca del cliente, no una
 * decisión de la combinación visual.
 */
export type StyleCombinationBaseline = {
  id: string
  colors: PageTheme['colors']
  roles: PageTheme['roles']
  roleBreakpoints?: PageTheme['roleBreakpoints']
  colorComponents: GlobalColorComponent[]
  typographyComponents: GlobalTypographyComponent[]
  buttonComponents: GlobalButtonComponent[]
  cardComponents: GlobalCardComponent[]
  sectionComponents: GlobalSectionComponent[]
  iconComponents: GlobalIconComponent[]
}

export const DEFAULT_BRAND_LOGO: BrandLogo = {
  name: '',
  nameFont: '',
  nameColor: 'var(--wb-text)',
  nameColorDark: '#ffffff',
  /* 20 px: el nombre junto al símbolo de 18, sin que la barra crezca. */
  nameSize: '20px',
  nameWeight: '',
  mark: 'icon',
  iconName: 'sparkles',
  iconColor: 'var(--wb-text)',
  iconColorDark: '#ffffff',
  iconSize: '18px',
  iconBackground: 'var(--wb-primary)',
  iconBackgroundDark: 'var(--wb-primary)',
  iconBackgroundOpacity: 100,
  iconRadius: '',
  imageLight: '',
  imageDark: '',
  imageSize: '120px',
  showName: true,
}

export type ProjectTheme = PageTheme & {
  brandLogo: BrandLogo
  colorComponents: GlobalColorComponent[]
  typographyComponents: GlobalTypographyComponent[]
  buttonComponents: GlobalButtonComponent[]
  cardComponents: GlobalCardComponent[]
  sectionComponents: GlobalSectionComponent[]
  iconComponents: GlobalIconComponent[]
  /** Última combinación elegida y el punto al que vuelve Restart. */
  styleCombinationBaseline?: StyleCombinationBaseline
  /**
   * `false`: el color de tarjeta es el que eligió el cliente y ninguna sección
   * lo cambia. Sin el campo (por defecto), cada sección ajusta el tono de sus
   * tarjetas para que se distingan de su fondo (`cardSurfaceOn`).
   */
  cardSurfaceAuto?: false
  /**
   * `true`: las secciones de fondo normal o secundario se intercalan solas
   * según su sitio en la página —al mover, agregar o quitar una, se recolocan—,
   * salvo las que alguien fijó a mano. Ver `withAutoSectionTones` en el editor.
   * Sin el campo, apagado: los proyectos de antes conservan su aspecto.
   */
  sectionToneAuto?: true
  /**
   * El movimiento del sitio (Marca › Animación): cada elemento hereda su
   * animación según lo que es. Sin el campo, nada se mueve: los proyectos de
   * antes conservan su aspecto. Ver `motion.ts`.
   */
  motion?: SiteMotion
}
